import { useEffect, useState } from "react";
import { z } from "zod";

interface UseLocalStorageStateOptions<T> {
  schema?: z.ZodType<T>;
}

export const useLocalStorageState = <T>(
  key: string,
  defaultValue: T,
  options?: UseLocalStorageStateOptions<T>
) => {
  const [state, setState] = useState<T>(() => {
    const storedValue = localStorage.getItem(key);
    if (storedValue === null) {
      return defaultValue;
    }

    try {
      const data: unknown = JSON.parse(storedValue);

      if (options?.schema) {
        const result = options.schema.safeParse(data);
        if (!result.success) {
          console.warn(
            `localStorage key "${key}" failed schema validation, using default value`,
            result.error.issues
          );
          return defaultValue;
        }
        return result.data;
      }

      return data as T;
    } catch {
      console.warn(
        `localStorage key "${key}" has invalid JSON, using default value`
      );
      return defaultValue;
    }
  });

  useEffect(() => {
    localStorage.setItem(key, JSON.stringify(state));
  }, [key, state]);

  return [state, setState] as const;
};
